// Form response sets ("form-choices") — reusable answer lists that choice-list
// questions pick from. GET /form-choices/all lists them; POST /form-choices/
// create adds one; PATCH /form-choices/update/:id edits one; POST /form-choices/
// bulk-delete removes any number in a single call. List response is confirmed
// against the backend (bare array of sets), so the raw types state the contract
// strictly.
//
// `detail` is registered in routes.ts and deliberately unused: the list response
// already carries `name` and `choices`, which is everything the editor needs, so
// opening a set for edit costs no extra request.
//
// There is no duplicate endpoint — a copy is a create carrying the source's own
// choices, assembled at the call site.

import { baseApi } from '../baseApi';
import { ROUTES } from '../routes';
import type { FormPerson } from './formsApi';

/** One answer of a response set. `color` is an 8-digit hex (#rrggbbaa), same
 *  idiom as the builder's checkList optionList; `value` feeds scoring. */
export interface ResponseSetChoice {
  label: string;
  value: string;
  color: string;
}

/** A reusable response set (form-choices row). */
export interface ResponseSet {
  id: string;
  name: string;
  choices: ResponseSetChoice[];
  updatedAt: string | null;
  updatedBy: FormPerson | null;
}

/** POST /form-choices/create + PATCH /form-choices/update/:id body. */
export interface ResponseSetPayload {
  title: string;
  choices: ResponseSetChoice[];
}

// ---- raw shapes (per the confirmed list response) ----
interface ApiPerson {
  _id: string;
  user: { firstName: string; lastName: string };
}
interface ApiResponseSet {
  _id: string;
  title: string;
  choices: ResponseSetChoice[];
  updatedAt: string | null;
  updatedBy: ApiPerson | null;
}

const toPerson = (p: ApiPerson | null): FormPerson | null => {
  if (!p) return null;
  const fullName = `${p.user.firstName} ${p.user.lastName}`.trim();
  return fullName ? { id: p._id, fullName } : null;
};

const toResponseSet = (r: ApiResponseSet): ResponseSet => ({
  id: r._id,
  name: r.title,
  choices: r.choices,
  updatedAt: r.updatedAt ?? null,
  updatedBy: toPerson(r.updatedBy),
});

export const formChoicesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getFormChoices: build.query<ResponseSet[], void>({
      query: () => ({ ...ROUTES.formChoices.all }),
      transformResponse: (res: ApiResponseSet[]) => res.map(toResponseSet),
      providesTags: [{ type: 'FormChoices', id: 'LIST' }],
    }),
    createFormChoice: build.mutation<unknown, ResponseSetPayload>({
      query: (body) => ({ ...ROUTES.formChoices.create, data: body }),
      invalidatesTags: [{ type: 'FormChoices', id: 'LIST' }],
    }),
    updateFormChoice: build.mutation<unknown, { id: string } & ResponseSetPayload>({
      query: ({ id, ...body }) => ({ ...ROUTES.formChoices.update(id), data: body }),
      invalidatesTags: [{ type: 'FormChoices', id: 'LIST' }],
    }),
    // Takes the ids directly and wraps them in the body's `formChoiceIds` key
    // here, so no call site has to know the envelope.
    bulkDeleteFormChoices: build.mutation<unknown, string[]>({
      query: (formChoiceIds) => ({ ...ROUTES.formChoices.bulkDelete, data: { formChoiceIds } }),
      invalidatesTags: [{ type: 'FormChoices', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetFormChoicesQuery,
  useCreateFormChoiceMutation,
  useUpdateFormChoiceMutation,
  useBulkDeleteFormChoicesMutation,
} = formChoicesApi;
